'use client';

import { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';

import { Spinner } from '@air/primitive-spinner';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const button = tailwindVariants({
  base: 'flex items-center justify-center gap-2 rounded-8 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-9 focus:ring-offset-2',
  defaultVariants: {
    appearance: 'outline',
    size: 'medium',
  },
  variants: {
    appearance: {
      ghost: 'bg-transparent text-neutral-12 hover:bg-neutral-3 disabled:text-neutral-8',
      outline:
        'border border-neutral-6 bg-white text-neutral-12 hover:bg-neutral-2 disabled:border-neutral-5 disabled:text-neutral-8 dark:border-neutral-7 dark:bg-neutral-2 dark:disabled:border-neutral-6 dark:hover:bg-neutral-3',
      primary: 'bg-brand-9 text-white hover:bg-brand-10 disabled:bg-neutral-8 disabled:text-neutral-1',
      secondary: 'bg-neutral-12 text-white hover:bg-neutral-11 disabled:bg-neutral-8 disabled:text-neutral-1',
    },
    size: {
      large: 'h-10 px-4 text-14',
      medium: 'h-8 px-3 text-14',
      small: 'h-6 px-2 text-12',
      xlarge: 'h-12 px-5 text-16',
    },
  },
});

export type ButtonVariants = VariantProps<typeof button>;

export type ButtonProps = ButtonVariants &
  Omit<ComponentPropsWithoutRef<'button'>, 'prefix'> & {
    isLoading?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      appearance,
      children,
      className,
      isLoading,
      prefix,
      suffix,
      size = 'medium',
      type = 'button',
      disabled,
      ...restOfProps
    },
    forwardedRef,
  ) => {
    let leading: ReactNode = null;

    if (isLoading) {
      leading = <Spinner className="size-4" />;
    } else if (prefix) {
      leading = <span className="shrink-0">{prefix}</span>;
    }

    return (
      <button
        className={button({ appearance, className, size })}
        disabled={disabled || isLoading}
        type={type}
        {...restOfProps}
        ref={forwardedRef}
      >
        {leading}
        <span>{children}</span>
        {suffix && !isLoading && <span className="shrink-0">{suffix}</span>}
      </button>
    );
  },
);

Button.displayName = 'Button';
